import { Box, CircularProgress, Paper, Table, TableBody, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import { useCreateInvoiceContext } from "../../../invoice/create/context/hooks/useCreateContext";
import { useGetEventScheduler } from "../../../jobs/hooks/useGetEventScheduler";
import { normalizeCostOverride } from "../../../jobs/jobs-list/jobs-layout/components/Summary/BasicJob";
import { invoiceEditorSectionCardSx } from "../../../ui/invoiceEditorSectionSurface";
import { SalesResponse, useGetSales } from "../../hooks/useGetSales";
import { useGetScopeOfWork } from "../../hooks/useGetScopeOfWork";
import { ScopeOfWork } from "../../types/scope-of-work.interface";
import { TSquareFootage } from "../../types/square-footage";
import { EMPTY_SALES_RESPONSE } from "../../utils/emptySalesResponse";
import { AreasList } from "./components/AreasList";
import { ServicePrincipalRow } from "./components/ServicePrincipalRow";
import { TableColumns } from "./components/TableColumns";
import { TableHeader } from "./components/TableHeader";
import { TotalsSection } from "./components/TotalsSection";
import {
  calculateComputedGrandTotal,
  calculateTotalFromSalesData,
  computeMainAreasLineTotal,
  getMainLineComputedPricing,
  roundMoney,
} from "./components/calculateTotal";
import { computeInvoiceMainLineSeed } from "./components/computeInvoiceMainLineSeed";
import { computeInvoiceLineAfterDepositCredit } from "./components/invoiceLineFromCostOverride";

import type { InvoiceDiscountApplyToOption, InvoiceDiscountDraft } from "../../../invoice/create/components/InvoiceDiscountsCard";
import type { InvoiceServiceFeeDraft } from "../../../invoice/create/components/InvoiceServiceFeesCard";

interface ListItemTableProps {
  scopeOfWork?: ScopeOfWork;
  scopeOfWorkUid?: string;
  eventSchedulerUid?: string;
  showTotals?: boolean;
  allowEdit?: boolean;
  /** When creating an invoice from a job with a paid deposit, remaining pool to credit (from /quotes/:id/deposit-available). */
  invoiceDepositAvailable?: number | null;
  invoiceDepositInvoiceUid?: string | null;
  /** Credited toward this invoice (parent depositCredit); takes precedence over pool for line + totals. */
  invoiceDepositCreditApplied?: number | null;
  /** When costOverride does not yield a gross (e.g. some basic quotes), use quote line total from the job payload. */
  invoiceLineGrossFallback?: number | null;
  /** Extra line items total from invoice editor (shown in TotalsSection display). */
  invoiceExtraLineItemsAmount?: number;
  invoiceDiscountsAmount?: number;
  invoiceServiceFeesAmount?: number;
  /** Hide the inline service-fee editor; fees are edited in a dedicated invoice section. */
  hideServiceFee?: boolean;
  serviceFees?: InvoiceServiceFeeDraft[];
  serviceFeeApplyToOptions?: InvoiceDiscountApplyToOption[];
  serviceFeeComputedAmounts?: Record<string, number>;
  onAddServiceFee?: (draft?: Omit<InvoiceServiceFeeDraft, "id">) => void;
  onRemoveServiceFee?: (id: string) => void;
  onChangeServiceFee?: (id: string, patch: Partial<InvoiceServiceFeeDraft>) => void;
  showServiceFeeValidation?: boolean;
  discounts?: InvoiceDiscountDraft[];
  discountApplyToOptions?: InvoiceDiscountApplyToOption[];
  discountComputedAmounts?: Record<string, number>;
  onAddDiscount?: (draft?: Omit<InvoiceDiscountDraft, "id">) => void;
  onRemoveDiscount?: (id: string) => void;
  onChangeDiscount?: (id: string, patch: Partial<InvoiceDiscountDraft>) => void;
  showDiscountValidation?: boolean;
  /** Rendered after the last service row (e.g. extra line items). */
  afterLineItems?: ReactNode;
  billedVisitGroups?: Array<{
    jobId: number;
    jobLabel: string;
    rows: Array<{
      uid: string;
      title: string;
      subtitle: string;
    }>;
  }>;
}

export const ListItemTable = ({
  scopeOfWork,
  scopeOfWorkUid,
  showTotals = true,
  eventSchedulerUid,
  allowEdit = false,
  invoiceDepositAvailable,
  invoiceDepositInvoiceUid,
  invoiceDepositCreditApplied,
  invoiceLineGrossFallback,
  invoiceExtraLineItemsAmount,
  invoiceDiscountsAmount,
  invoiceServiceFeesAmount,
  hideServiceFee = false,
  serviceFees,
  serviceFeeApplyToOptions,
  serviceFeeComputedAmounts,
  onAddServiceFee,
  onRemoveServiceFee,
  onChangeServiceFee,
  showServiceFeeValidation = false,
  discounts,
  discountApplyToOptions,
  discountComputedAmounts,
  onAddDiscount,
  onRemoveDiscount,
  onChangeDiscount,
  showDiscountValidation = false,
  afterLineItems,
  billedVisitGroups,
}: ListItemTableProps) => {
  // State for expanded accordions (area UIDs)
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());
  const isXs = useMediaQuery("(max-width:600px)");
  // State for expanded service principal (to show/hide areas)
  const [isServiceExpanded, setIsServiceExpanded] = useState<boolean>(false);

  const costOverride = useMemo(() => {
    if (!scopeOfWork?.costOverride) return undefined;
    return normalizeCostOverride(scopeOfWork?.costOverride);
  }, [scopeOfWork?.costOverride]);

  const pricingOpts = { zeroLineDefaults: allowEdit };

  const grossFromCostOverrideRef = useRef<number | null>(null);

  const { state, controller } = useCreateInvoiceContext();
  const company = useSelector((state: RootState) => state.userState.company);
  const companyGlobalFeeLabel = (company as any)?.invoiceCustomStyle?.labels?.increasePercentageByFrequency;

  // State for editable values (for invoice) - simple object to track edited values
  const overrideUnit = useMemo(() => {
    const o = costOverride as { value?: unknown; price?: unknown; amount?: unknown } | null | undefined;
    if (!o) return undefined;
    const n = Number(o.value ?? o.price ?? o.amount ?? 0);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [costOverride]);

  const [editedValues, setEditedValues] = useState<{
    servicePrice?: number | null;
    serviceTotal?: number | null;
    finalTotal?: number | null;
    recurringFee?: number | null;
    recurringFeeLabel?: string;
    time?: number | null;
    isServiceLineTotalOverridden?: boolean;
    isGlobalTotalOverridden?: boolean;
  }>({
    servicePrice: overrideUnit ?? (allowEdit ? 0 : undefined),
    recurringFeeLabel: undefined,
    time: (() => {
      const raw = costOverride?.units;
      const u = raw != null && raw !== "" ? Number(raw) : NaN;
      if (Number.isFinite(u) && u > 0) return u * 60;
      if (allowEdit && overrideUnit != null) return 60;
      return allowEdit ? 0 : undefined;
    })(),
    recurringFee: allowEdit ? 0 : undefined,
    isServiceLineTotalOverridden: false,
    isGlobalTotalOverridden: false,
  });

  // State for configuration values that affect cost calculation
  const [travelBufferTime, setTravelBufferTime] = useState<number | undefined>(undefined);
  const [travelBufferTimeInput, setTravelBufferTimeInput] = useState<string>("");
  const [frequencyType, setFrequencyType] = useState<string | undefined>(undefined);

  // Event Scheduler
  const { data: eventScheduler } = useGetEventScheduler({
    eventSchedulerUid: eventSchedulerUid ?? "",
  });

  // Always call the hook to satisfy React hooks rules, but disable query if we have scopeOfWork prop
  const { data: fetchedScopeOfWork, isLoading: isLoadingScopeOfWork } = useGetScopeOfWork({
    scopeOfWorkUid: scopeOfWorkUid ?? "",
  });

  // Use provided scopeOfWork or fetched one
  const localScopeOfWork: ScopeOfWork = scopeOfWork || fetchedScopeOfWork;

  // Initialize from scopeOfWork (buffer / frequency for sales hook). Do not merge invoice context into
  // editedValues when allowEdit — defaults stay at 0 (no backend/context prefill).
  useEffect(() => {
    if (localScopeOfWork) {
      if (travelBufferTime === undefined && localScopeOfWork.bufferTime !== undefined && localScopeOfWork.bufferTime !== null) {
        setTravelBufferTime(localScopeOfWork.bufferTime);
        setTravelBufferTimeInput(String(localScopeOfWork.bufferTime));
      }
      if (frequencyType === undefined && localScopeOfWork.frequencyType) {
        setFrequencyType(localScopeOfWork.frequencyType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localScopeOfWork]);

  useEffect(() => {
    if (state.serviceValues?.serviceFeeLabel && state.serviceValues.serviceFeeLabel !== editedValues.recurringFeeLabel) {
      setEditedValues((prev) => ({
        ...prev,
        recurringFeeLabel: state.serviceValues.serviceFeeLabel,
      }));
    }
  }, [state.serviceValues?.serviceFeeLabel, editedValues.recurringFeeLabel]);

  useEffect(() => {
    if (!hideServiceFee) return;
    setEditedValues((prev) => (prev.recurringFee ? { ...prev, recurringFee: 0 } : prev));
  }, [hideServiceFee]);

  const invoiceMainLineSeed = useMemo(() => {
    if (!allowEdit || !localScopeOfWork) return null;
    return computeInvoiceMainLineSeed(localScopeOfWork, invoiceLineGrossFallback);
  }, [allowEdit, localScopeOfWork, invoiceLineGrossFallback]);

  // Seed gross line total from costOverride (fixes Total 0 when qty/time was unset but unit price exists).
  useEffect(() => {
    if (!allowEdit || !invoiceMainLineSeed) {
      grossFromCostOverrideRef.current = null;
      return;
    }
    const { servicePrice, timeMinutes, gross } = invoiceMainLineSeed;
    grossFromCostOverrideRef.current = gross;

    setEditedValues((prev) => {
      if (prev.isServiceLineTotalOverridden && prev.serviceTotal != null && prev.serviceTotal > 0) {
        return prev;
      }
      let nextTime = prev.time != null && prev.time > 0 ? prev.time : timeMinutes;
      let nextPrice = prev.servicePrice != null && prev.servicePrice > 0 ? prev.servicePrice : servicePrice;
      if (gross > 0 && nextTime <= 0) nextTime = timeMinutes;
      if (gross > 0 && nextPrice <= 0) nextPrice = servicePrice;

      return {
        ...prev,
        servicePrice: nextPrice,
        time: nextTime,
        serviceTotal: gross,
        isServiceLineTotalOverridden: true,
      };
    });
  }, [allowEdit, invoiceMainLineSeed]);

  const visits = useMemo(() => {
    if (!billedVisitGroups || billedVisitGroups.length === 0) return [];
    return billedVisitGroups[0]?.rows ?? [];
  }, [billedVisitGroups]);

  const [eventValues, setEventValues] = useState<
    Record<
      string,
      {
        servicePrice?: number | null;
        time?: number | null;
        serviceTotal?: number | null;
        isServiceLineTotalOverridden?: boolean;
      }
    >
  >({});

  const getEventValues = useMemo(() => {
    return (eventUid: string) => {
      const ev = eventValues[eventUid] ?? {};
      const {
        servicePrice: seedPrice,
        timeMinutes: seedTime,
        gross: seedGross,
      } = invoiceMainLineSeed ?? {
        servicePrice: 0,
        timeMinutes: 0,
        gross: 0,
      };

      const time = ev.time !== undefined ? ev.time : (editedValues.time ?? seedTime);
      const servicePrice = ev.servicePrice !== undefined ? ev.servicePrice : (editedValues.servicePrice ?? seedPrice);

      const qtyHours = (time ?? 0) / 60;
      const computedLineTotal = roundMoney(qtyHours * (servicePrice ?? 0));

      const serviceTotal = ev.isServiceLineTotalOverridden
        ? (ev.serviceTotal ?? 0)
        : ev.serviceTotal !== undefined
          ? ev.serviceTotal
          : editedValues.isServiceLineTotalOverridden
            ? (editedValues.serviceTotal ?? computedLineTotal)
            : computedLineTotal;

      const isServiceLineTotalOverridden =
        ev.isServiceLineTotalOverridden !== undefined ? ev.isServiceLineTotalOverridden : (editedValues.isServiceLineTotalOverridden ?? false);

      return {
        time,
        servicePrice,
        serviceTotal: roundMoney(serviceTotal),
        isServiceLineTotalOverridden,
      };
    };
  }, [eventValues, editedValues, invoiceMainLineSeed]);

  // Seed event-specific values for each visit from the main line seed
  useEffect(() => {
    if (!allowEdit || !invoiceMainLineSeed || !visits.length) return;
    const { servicePrice, timeMinutes, gross } = invoiceMainLineSeed;
    setEventValues((prev) => {
      const next = { ...prev };
      let changed = false;
      visits.forEach((visit) => {
        if (!next[visit.uid]) {
          next[visit.uid] = {
            servicePrice,
            time: timeMinutes,
            serviceTotal: gross,
            isServiceLineTotalOverridden: true,
          };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [allowEdit, invoiceMainLineSeed, visits]);

  const handleEventValueChange = (
    eventUid: string,
    updates: {
      servicePrice?: number | null;
      serviceTotal?: number | null;
      time?: number | null;
      isServiceLineTotalOverridden?: boolean;
    },
  ) => {
    setEventValues((prev) => {
      const currentEventVal = prev[eventUid] ?? {};

      const seed = invoiceMainLineSeed ?? {
        servicePrice: 0,
        timeMinutes: 0,
      };
      const currentTime = currentEventVal.time !== undefined ? currentEventVal.time : (editedValues.time ?? seed.timeMinutes);
      const currentPrice =
        currentEventVal.servicePrice !== undefined ? currentEventVal.servicePrice : (editedValues.servicePrice ?? seed.servicePrice);

      const nextTime = updates.time !== undefined ? updates.time : currentTime;
      const nextPrice = updates.servicePrice !== undefined ? updates.servicePrice : currentPrice;

      const qtyHours = (nextTime ?? 0) / 60;
      const computedLineTotal = roundMoney(qtyHours * (nextPrice ?? 0));

      let nextTotal = updates.serviceTotal !== undefined ? updates.serviceTotal : currentEventVal.serviceTotal;
      let nextOverridden =
        updates.isServiceLineTotalOverridden !== undefined ? updates.isServiceLineTotalOverridden : currentEventVal.isServiceLineTotalOverridden;

      if (updates.time !== undefined || updates.servicePrice !== undefined) {
        nextTotal = computedLineTotal;
        nextOverridden = false;
      }

      if (updates.isServiceLineTotalOverridden === false) {
        nextTotal = computedLineTotal;
      }

      const nextEventVal = {
        ...currentEventVal,
        ...updates,
        time: nextTime,
        servicePrice: nextPrice,
        serviceTotal: nextTotal !== undefined ? roundMoney(nextTotal) : undefined,
        isServiceLineTotalOverridden: nextOverridden,
      };

      return {
        ...prev,
        [eventUid]: nextEventVal,
      };
    });

    setEditedValues((prev) => {
      if (prev.isGlobalTotalOverridden) {
        const next = { ...prev };
        next.isGlobalTotalOverridden = false;
        delete next.finalTotal;
        return next;
      }
      return prev;
    });
  };

  // After deposit pool / credited amount loads, default line Total to remaining balance (user can still override).
  useEffect(() => {
    if (!allowEdit) return;
    const gross = grossFromCostOverrideRef.current;
    if (gross == null || gross <= 0) return;

    const lineDefault = computeInvoiceLineAfterDepositCredit(
      gross,
      invoiceDepositInvoiceUid ?? null,
      invoiceDepositAvailable,
      invoiceDepositCreditApplied,
    );

    setEditedValues((prev) => {
      if (!prev.isServiceLineTotalOverridden || prev.serviceTotal == null) return prev;
      const t = prev.serviceTotal;
      if (Math.abs(t - gross) > 0.009) return prev;
      return { ...prev, serviceTotal: lineDefault, isServiceLineTotalOverridden: true };
    });
  }, [allowEdit, invoiceDepositAvailable, invoiceDepositInvoiceUid, invoiceDepositCreditApplied]);

  useEffect(() => {
    if (allowEdit) return;
    const contextUpdates: Record<string, unknown> = {};
    let hasContextValues = false;
    if (state.serviceValues.servicePrice !== undefined) {
      contextUpdates.servicePrice = state.serviceValues.servicePrice;
      hasContextValues = true;
    }
    if (state.serviceValues.time !== undefined) {
      contextUpdates.time = state.serviceValues.time;
      hasContextValues = true;
    }
    if (hasContextValues) {
      setEditedValues((prev) => ({ ...prev, ...contextUpdates }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localScopeOfWork, allowEdit]);

  const { data: salesData, isLoading: isLoadingSales } = useGetSales({
    areas: localScopeOfWork?.areasByScopesOfWork ?? [],
    typeOfService: localScopeOfWork?.typeOfService
      ? {
          costBias: localScopeOfWork.typeOfService.costBias ?? 1,
          baseRatePerHour: localScopeOfWork.typeOfService.baseRatePerHour ?? 0,
        }
      : undefined,
    squareFootage: localScopeOfWork?.typeOfPlace?.squareFootage as unknown as TSquareFootage,
    travelBufferTimeInGeneral: travelBufferTime ?? localScopeOfWork?.bufferTime,
    frequencyType: frequencyType ?? localScopeOfWork?.frequencyType,
  });

  // Type assertion for salesData
  const typedSalesData = salesData as SalesResponse | undefined;
  const effectiveSalesData: SalesResponse | undefined = typedSalesData ?? (allowEdit ? EMPTY_SALES_RESPONSE : undefined);

  const handleToggleAccordion = (areaUid: string) => {
    setExpandedAreas((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(areaUid)) {
        newSet.delete(areaUid);
      } else {
        newSet.add(areaUid);
      }
      return newSet;
    });
  };

  const mainLineTotalSum = useMemo(() => {
    if (!visits.length) {
      return computeMainAreasLineTotal(effectiveSalesData, editedValues, pricingOpts);
    }
    return visits.reduce((sum, visit) => {
      const ev = getEventValues(visit.uid);
      return sum + computeMainAreasLineTotal(effectiveSalesData, ev, pricingOpts);
    }, 0);
  }, [visits, effectiveSalesData, getEventValues, editedValues, pricingOpts]);

  const timeSum = useMemo(() => {
    if (!visits.length) return editedValues.time ?? 0;
    return visits.reduce((sum, visit) => {
      const ev = getEventValues(visit.uid);
      return sum + (ev.time ?? 0);
    }, 0);
  }, [visits, editedValues.time, getEventValues]);

  const effectiveEditedValuesForTotals = useMemo(() => {
    if (!visits.length) return editedValues;
    return {
      ...editedValues,
      serviceTotal: mainLineTotalSum,
      isServiceLineTotalOverridden: true,
    };
  }, [visits, editedValues, mainLineTotalSum]);

  const total = effectiveSalesData ? calculateTotalFromSalesData(effectiveSalesData, effectiveEditedValuesForTotals, pricingOpts) : 0;

  const computedGrandTotal =
    effectiveSalesData != null
      ? calculateComputedGrandTotal(
          effectiveSalesData,
          {
            ...effectiveEditedValuesForTotals,
            isGlobalTotalOverridden: false,
          },
          pricingOpts,
        )
      : 0;

  const { effectiveUnitPrice, computedLineTotal } =
    effectiveSalesData != null
      ? getMainLineComputedPricing(
          effectiveSalesData,
          {
            time: visits.length > 0 ? timeSum : (editedValues.time ?? 0),
            servicePrice: visits.length > 0 ? (timeSum > 0 ? roundMoney(mainLineTotalSum / (timeSum / 60)) : 0) : (editedValues.servicePrice ?? 0),
          },
          pricingOpts,
        )
      : { effectiveUnitPrice: 0, computedLineTotal: 0 };

  const mainLineAmount = effectiveSalesData != null ? computeMainAreasLineTotal(effectiveSalesData, effectiveEditedValuesForTotals, pricingOpts) : 0;

  // Sync pricing state to create-invoice context (payload + calculations)
  useEffect(() => {
    const serviceLines =
      visits.length > 0
        ? visits.map((visit) => {
            const ev = getEventValues(visit.uid);
            return {
              eventUid: visit.uid,
              qty: roundMoney((ev.time ?? 0) / 60),
              unitPrice: ev.servicePrice ?? 0,
              lineTotal: ev.serviceTotal ?? 0,
              isLineTotalOverridden: ev.isServiceLineTotalOverridden,
            };
          })
        : undefined;

    const effectiveFeeLabel = editedValues.recurringFeeLabel || state.serviceValues?.serviceFeeLabel || companyGlobalFeeLabel || "Service fee";

    controller.setServiceValues({
      subTotal: total,
      total,
      servicePrice: visits.length > 0 ? (timeSum > 0 ? roundMoney(mainLineTotalSum / (timeSum / 60)) : 0) : editedValues.servicePrice,
      time: visits.length > 0 ? timeSum : editedValues.time,
      serviceTotal: visits.length > 0 ? mainLineTotalSum : editedValues.serviceTotal,
      isServiceLineTotalOverridden: visits.length > 0 ? true : editedValues.isServiceLineTotalOverridden,
      finalTotal: editedValues.finalTotal,
      isGlobalTotalOverridden: editedValues.isGlobalTotalOverridden,
      serviceFee: editedValues.recurringFee ?? 0,
      serviceFeeLabel: effectiveFeeLabel,
      effectiveLineUnitPrice:
        effectiveSalesData != null
          ? visits.length > 0
            ? timeSum > 0
              ? roundMoney(mainLineTotalSum / (timeSum / 60))
              : 0
            : effectiveUnitPrice
          : null,
      computedLineTotal: effectiveSalesData != null ? (visits.length > 0 ? mainLineTotalSum : computedLineTotal) : null,
      computedGrandTotal: effectiveSalesData != null ? computedGrandTotal : null,
      serviceLines,
    });
  }, [
    total,
    controller,
    effectiveSalesData,
    effectiveUnitPrice,
    computedLineTotal,
    computedGrandTotal,
    editedValues.servicePrice,
    editedValues.time,
    editedValues.serviceTotal,
    editedValues.isServiceLineTotalOverridden,
    editedValues.finalTotal,
    editedValues.isGlobalTotalOverridden,
    editedValues.recurringFee,
    editedValues.recurringFeeLabel,
    companyGlobalFeeLabel,
    visits,
    eventValues,
    getEventValues,
    mainLineTotalSum,
    timeSum,
  ]);

  const handleServiceValueChange = (updates: {
    servicePrice?: number | null;
    serviceTotal?: number | null;
    time?: number | null;
    isServiceLineTotalOverridden?: boolean;
  }) => {
    setEditedValues((prev) => {
      const seed = invoiceMainLineSeed ?? {
        servicePrice: 0,
        timeMinutes: 0,
      };
      const currentTime = updates.time !== undefined ? updates.time : prev.time !== undefined ? prev.time : seed.timeMinutes;
      const currentPrice =
        updates.servicePrice !== undefined ? updates.servicePrice : prev.servicePrice !== undefined ? prev.servicePrice : seed.servicePrice;

      const qtyHours = (currentTime ?? 0) / 60;
      const computedLineTotal = roundMoney(qtyHours * (currentPrice ?? 0));

      let nextTotal = updates.serviceTotal !== undefined ? updates.serviceTotal : prev.serviceTotal;
      let nextOverridden =
        updates.isServiceLineTotalOverridden !== undefined ? updates.isServiceLineTotalOverridden : prev.isServiceLineTotalOverridden;

      if (updates.time !== undefined || updates.servicePrice !== undefined) {
        nextTotal = computedLineTotal;
        nextOverridden = false;
      }

      if (updates.isServiceLineTotalOverridden === false) {
        nextTotal = computedLineTotal;
      }

      const next = {
        ...prev,
        ...updates,
        time: currentTime,
        servicePrice: currentPrice,
        serviceTotal: nextTotal,
        isServiceLineTotalOverridden: nextOverridden,
      };
      // Line totals drive the breakdown; invalidate manual global total when the row changes
      if (prev.isGlobalTotalOverridden) {
        next.isGlobalTotalOverridden = false;
        delete next.finalTotal;
      }
      return next;
    });
  };

  const handleGlobalTotalChange = (value: number | null) => {
    if (value == null) return;
    setEditedValues((prev) => ({
      ...prev,
      finalTotal: roundMoney(value),
      isGlobalTotalOverridden: true,
    }));
  };

  const handleGlobalTotalReset = () => {
    setEditedValues((prev) => {
      const next = { ...prev };
      delete next.finalTotal;
      next.isGlobalTotalOverridden = false;
      return next;
    });
  };

  const handleRecurringFeeChange = (value: number | null) => {
    const fee = value ?? 0;
    controller.setServiceValues({ serviceFee: fee });
    setEditedValues((prev) => {
      const next = { ...prev, recurringFee: fee };
      if (prev.isGlobalTotalOverridden) {
        next.isGlobalTotalOverridden = false;
        delete next.finalTotal;
      }
      return next;
    });
  };

  const handleRecurringFeeLabelChange = (label: string) => {
    controller.setServiceValues({ serviceFeeLabel: label });
    setEditedValues((prev) => ({
      ...prev,
      recurringFeeLabel: label,
    }));
  };

  // ResettableInput handles initialization internally - no need for manual string state management

  if (isLoadingScopeOfWork) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (!localScopeOfWork) {
    return (
      <Paper elevation={0} sx={{ ...invoiceEditorSectionCardSx, p: 3 }}>
        <Typography>No scope of work data available</Typography>
      </Paper>
    );
  }

  // Invoice edit can render totals from edited line values while sales loads or if getSales fails.
  if (isLoadingSales && !typedSalesData && !allowEdit) {
    return (
      <Paper elevation={0} sx={invoiceEditorSectionCardSx}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (!effectiveSalesData) {
    return (
      <Paper elevation={0} sx={{ ...invoiceEditorSectionCardSx, p: 3 }}>
        <Typography>Unable to load service pricing. Check connection and try again.</Typography>
      </Paper>
    );
  }

  const areas = localScopeOfWork.areasByScopesOfWork || [];
  const typeOfService = localScopeOfWork.typeOfService;

  const getAreaTime = (areaUid: string): string | undefined => {
    if (!effectiveSalesData?.mainAreas?.areas) return undefined;
    const salesArea = effectiveSalesData.mainAreas.areas.find((a: any) => a.areaUid === areaUid);
    return salesArea?.time?.formatted;
  };

  return (
    <Paper elevation={0} sx={invoiceEditorSectionCardSx}>
      <TableHeader />

      <Box sx={{ overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "100%", minWidth: allowEdit ? 680 : 600 }}>
          {isXs ? null : <TableColumns />}
          <TableBody>
            {typeOfService &&
              (visits.length > 0 ? (
                visits.map((visit, visitIndex) => {
                  const ev = getEventValues(visit.uid);
                  return (
                    <ServicePrincipalRow
                      serviceNameOverride={localScopeOfWork.serviceNameOverride}
                      key={visit.uid}
                      typeOfService={typeOfService}
                      squareFootage={localScopeOfWork?.typeOfPlace?.squareFootage}
                      salesData={effectiveSalesData}
                      isExpanded={isServiceExpanded}
                      onToggleExpand={() => setIsServiceExpanded(!isServiceExpanded)}
                      allowEdit={allowEdit}
                      editedValues={{
                        servicePrice: ev.servicePrice,
                        time: ev.time,
                        serviceTotal: ev.serviceTotal,
                        isServiceLineTotalOverridden: ev.isServiceLineTotalOverridden,
                      }}
                      onValueChange={(updates) => handleEventValueChange(visit.uid, updates)}
                      invoiceMainLineSeed={invoiceMainLineSeed}
                      visit={visit}
                      visitIndex={visitIndex}
                      serviceDescriptionOverride={localScopeOfWork.description}
                    />
                  );
                })
              ) : (
                <ServicePrincipalRow
                  typeOfService={typeOfService}
                  serviceNameOverride={localScopeOfWork.serviceNameOverride}
                  squareFootage={localScopeOfWork?.typeOfPlace?.squareFootage}
                  salesData={effectiveSalesData}
                  isExpanded={isServiceExpanded}
                  onToggleExpand={() => setIsServiceExpanded(!isServiceExpanded)}
                  allowEdit={allowEdit}
                  editedValues={editedValues}
                  onValueChange={handleServiceValueChange}
                  invoiceMainLineSeed={invoiceMainLineSeed}
                  serviceDescriptionOverride={localScopeOfWork.description}
                />
              ))}
            <AreasList
              areas={areas}
              isExpanded={isServiceExpanded}
              expandedAreas={expandedAreas}
              onToggleArea={handleToggleAccordion}
              getAreaTime={getAreaTime}
            />
          </TableBody>
        </Table>
      </Box>

      {afterLineItems}

      {showTotals && (
        <TotalsSection
          salesData={effectiveSalesData}
          mainLineAmount={mainLineAmount}
          total={total}
          computedGrandTotal={computedGrandTotal}
          isGlobalTotalOverridden={editedValues.isGlobalTotalOverridden === true}
          onGlobalTotalChange={handleGlobalTotalChange}
          onGlobalTotalReset={handleGlobalTotalReset}
          allowEdit={allowEdit}
          eventSchedulerFrequency={eventScheduler?.frequency}
          recurringFeeValue={editedValues.recurringFee ?? 0}
          onRecurringFeeChange={handleRecurringFeeChange}
          serviceFeeLabel={editedValues.recurringFeeLabel || state.serviceValues?.serviceFeeLabel || companyGlobalFeeLabel || "Service fee"}
          onServiceFeeLabelChange={handleRecurringFeeLabelChange}
          depositCreditAmount={invoiceDepositCreditApplied != null && invoiceDepositCreditApplied > 0 ? invoiceDepositCreditApplied : undefined}
          extraLineItemsAmount={invoiceExtraLineItemsAmount ?? 0}
          discountsAmount={invoiceDiscountsAmount ?? 0}
          serviceFeesAmount={invoiceServiceFeesAmount ?? 0}
          hideServiceFee={hideServiceFee}
          serviceFees={serviceFees}
          serviceFeeApplyToOptions={serviceFeeApplyToOptions}
          serviceFeeComputedAmounts={serviceFeeComputedAmounts}
          onAddServiceFee={onAddServiceFee}
          onRemoveServiceFee={onRemoveServiceFee}
          onChangeServiceFee={onChangeServiceFee}
          showServiceFeeValidation={showServiceFeeValidation}
          discounts={discounts}
          discountApplyToOptions={discountApplyToOptions}
          discountComputedAmounts={discountComputedAmounts}
          onAddDiscount={onAddDiscount}
          onRemoveDiscount={onRemoveDiscount}
          onChangeDiscount={onChangeDiscount}
          showDiscountValidation={showDiscountValidation}
        />
      )}
    </Paper>
  );
};
